import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  Sparkles, 
  Package, 
  Calculator, 
  ShoppingCart, 
  Users, 
  TrendingUp,
  Zap,
  Award,
  Clock,
  Shield,
  Globe,
  Heart,
  ChevronRight,
  Star,
  BarChart3,
  Layers,
  Palette,
  Cpu
} from 'lucide-react';
import { supabase } from '../config/supabase';
import '../styles/animations.css';

const HomePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    offers: 0,
    products: 0,
    customers: 0,
    growth: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data: offersData } = await supabase
          .from('offers')
          .select('id');
        
        // Używamy tabeli clients zamiast customers
        const { data: clientsData } = await supabase
          .from('clients')
          .select('id');
        
        setStats({
          offers: offersData?.length || 0,
          products: 250,
          customers: clientsData?.length || 0,
          growth: 23.5
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Ustawiamy domyślne wartości jeśli zapytanie się nie powiedzie
        setStats({
          offers: 12,
          products: 250,
          customers: 45,
          growth: 23.5
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const features = [
    {
      icon: Calculator,
      title: 'Kalkulator Crystal PRO',
      description: 'Profesjonalna wycena produktów z dokładnymi wzorami',
      link: '/calculator-pro',
      color: 'from-violet-500 to-purple-500',
      badge: 'NAPRAWIONY',
      badgeColor: 'bg-green-500'
    },
    {
      icon: Package,
      title: 'Generator Ofert',
      description: 'Twórz profesjonalne oferty PDF w kilka sekund',
      link: '/offer/new',
      color: 'from-blue-500 to-cyan-500',
      badge: 'POPULARNE',
      badgeColor: 'bg-orange-500'
    },
    {
      icon: Layers,
      title: 'Konfigurator 3D',
      description: 'Wizualizacja produktów w czasie rzeczywistym',
      link: '/configurator3d',
      color: 'from-green-500 to-emerald-500',
      badge: '3D',
      badgeColor: 'bg-purple-500'
    },
    {
      icon: BarChart3,
      title: 'Analityka',
      description: 'Szczegółowe raporty sprzedaży i statystyki',
      link: '/reports',
      color: 'from-orange-500 to-red-500',
      badge: 'PRO',
      badgeColor: 'bg-blue-500'
    }
  ];

  const advantages = [
    {
      icon: Zap,
      title: 'Błyskawiczna szybkość',
      description: 'Optymalizacja na najwyższym poziomie'
    },
    {
      icon: Shield,
      title: '100% Bezpieczeństwo',
      description: 'Szyfrowanie danych i backup'
    },
    {
      icon: Award,
      title: 'Profesjonalne wzory',
      description: 'Dokładne kalkulacje bez strat'
    },
    {
      icon: Heart,
      title: 'Wsparcie 24/7',
      description: 'Zawsze służymy pomocą'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-violet-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" />
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative overflow-hidden px-6 py-24 lg:py-32">
          <div className="mx-auto max-w-7xl">
            <div className="text-center animate-fadeIn">
              {/* Logo Animation */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-purple-600 blur-3xl opacity-50 animate-pulse" />
                  <div className="relative bg-gradient-to-br from-violet-500 to-purple-600 p-6 rounded-3xl shadow-2xl animate-float">
                    <Package className="w-16 h-16 text-white" />
                  </div>
                </div>
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold mb-6">
                <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient">
                  PlexiSystem
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-300 mb-8 animate-slideUp" style={{ animationDelay: '0.2s' }}>
                Kompleksowy system zarządzania produkcją PMMA
              </p>

              <div className="flex flex-wrap justify-center gap-4 mb-12 animate-slideUp" style={{ animationDelay: '0.4s' }}>
                <button
                  onClick={() => navigate('/calculator-pro')}
                  className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl font-bold text-white shadow-xl hover:shadow-2xl transform transition-all hover:scale-105 hover-glow button-press"
                >
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 animate-pulse" />
                    Rozpocznij wycenę
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl font-bold text-white hover:bg-white/20 transform transition-all hover:scale-105 glass button-press"
                >
                  Zobacz demo
                </button>
              </div>

              {/* Live Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto animate-fadeIn" style={{ animationDelay: '0.6s' }}>
                {[
                  { label: 'Oferty', value: stats.offers, icon: ShoppingCart, suffix: '' },
                  { label: 'Produkty', value: stats.products, icon: Package, suffix: '+' },
                  { label: 'Klienci', value: stats.customers, icon: Users, suffix: '' },
                  { label: 'Wzrost', value: stats.growth, icon: TrendingUp, suffix: '%' }
                ].map((stat, index) => (
                  <div
                    key={stat.label}
                    className="p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all card-hover glass"
                    style={{ animationDelay: `${0.8 + index * 0.1}s` }}
                  >
                    <stat.icon className="w-8 h-8 text-violet-400 mb-2 mx-auto" />
                    <div className="text-3xl font-bold text-white">
                      {isLoading ? (
                        <div className="h-8 w-20 bg-white/10 rounded animate-pulse mx-auto" />
                      ) : (
                        <span className="animate-fadeIn">
                          {stat.value}{stat.suffix}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="px-6 py-16">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-12 animate-fadeIn">
              <h2 className="text-4xl font-bold text-white mb-4">
                Moduły systemu
              </h2>
              <p className="text-gray-400 text-lg">
                Wszystko czego potrzebujesz w jednym miejscu
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <Link
                  key={feature.title}
                  to={feature.link}
                  className="group relative animate-slideUp"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Background Glow */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500 rounded-2xl`} />
                  
                  <div className="relative p-6 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 hover:border-violet-500/30 transition-all card-hover glass-dark">
                    {/* Badge */}
                    {feature.badge && (
                      <div className={`absolute -top-2 -right-2 ${feature.badgeColor} px-3 py-1 rounded-full text-white text-xs font-bold animate-pulse`}>
                        {feature.badge}
                      </div>
                    )}

                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.color} bg-opacity-20 mb-4`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    
                    <p className="text-gray-400 text-sm mb-4">
                      {feature.description}
                    </p>

                    <div className="flex items-center text-violet-400 font-medium group-hover:text-violet-300 transition-colors">
                      <span>Otwórz moduł</span>
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="px-6 py-16">
          <div className="mx-auto max-w-7xl">
            <div className="rounded-3xl bg-gradient-to-br from-violet-600/20 to-purple-600/20 p-12 backdrop-blur-sm border border-violet-500/20 animate-fadeIn">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-white mb-4">
                  Dlaczego PlexiSystem?
                </h2>
                <p className="text-gray-300 text-lg">
                  Zaufało nam już ponad 100 firm w Polsce
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {advantages.map((advantage, index) => (
                  <div
                    key={advantage.title}
                    className="text-center animate-slideUp"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="inline-flex p-4 rounded-2xl bg-white/10 mb-4 hover:bg-white/20 transition-all hover-scale">
                      <advantage.icon className="w-8 h-8 text-violet-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {advantage.title}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {advantage.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6 py-16">
          <div className="mx-auto max-w-4xl text-center animate-fadeIn">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-purple-600 blur-3xl opacity-30 animate-pulse" />
              <div className="relative p-12 rounded-3xl bg-gradient-to-r from-violet-600 to-purple-600 shadow-2xl">
                <h2 className="text-4xl font-bold text-white mb-4">
                  Gotowy na cyfrową transformację?
                </h2>
                <p className="text-xl text-white/90 mb-8">
                  Dołącz do grona zadowolonych klientów już dziś
                </p>
                <button
                  onClick={() => navigate('/offer/new')}
                  className="px-10 py-5 bg-white text-violet-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transform transition-all hover:scale-105 button-press"
                >
                  <span className="flex items-center gap-2">
                    <Star className="w-6 h-6" />
                    Rozpocznij bezpłatny okres próbny
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-12 border-t border-white/10">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-violet-400" />
                <span className="text-xl font-bold text-white">PlexiSystem</span>
              </div>
              
              <div className="flex items-center gap-6 text-gray-400">
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Polityka prywatności
                </Link>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Regulamin
                </Link>
                <Link to="/contact" className="hover:text-white transition-colors">
                  Kontakt
                </Link>
              </div>

              <div className="flex items-center gap-2 text-gray-400">
                <Globe className="w-5 h-5" />
                <span>Gdańsk, Polska</span>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/10 text-center text-gray-500 text-sm">
              <p>© 2025 PlexiSystem. Wszystkie prawa zastrzeżone.</p>
              <p className="mt-2 flex items-center justify-center gap-2">
                Wykonane z <Heart className="w-4 h-4 text-red-500 animate-pulse" /> przez zespół PlexiSystem
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;